/**
 * The shared archive every shareholder may read — 17 Sep 2026.
 *
 * The shareholder agreement, protokoller from general meetings: documents that
 * belong to all the holders rather than to one, which is what makes this a
 * collection of its own rather than more `attachments` on a record.
 *
 * Metadata in `skidaCrmShareholderDocs`, bytes in Storage under
 * `shareholderArchive/{id}`. An admin writes both from the browser; a
 * shareholder reads the metadata straight from Firestore and gets the bytes
 * through `crmShareholderArchive`, which is the only thing in the system
 * allowed to read that Storage path. See `storage.rules`.
 */
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore'
import { deleteObject, getStorage, ref, uploadBytes } from 'firebase/storage'
import { app, db } from '@/shared/firebase/client'
import { callFunctionForBlob } from '@/shared/firebase/callFunction'
import { todayIso, toIsoOrNull } from '@/shared/util/dates'

const DOCS = 'skidaCrmShareholderDocs'

/** PDF and nothing else — the rules say the same, and they are the ones that count. */
export const ARCHIVE_TYPE = 'application/pdf'
export const ARCHIVE_MAX_BYTES = 20 * 1024 * 1024

export interface ShareholderDoc {
  id: string
  /** What the file is called on screen and in the download. */
  name: string
  /**
   * The document's own date, ISO — the day of the meeting, not the day somebody
   * got round to filing it.
   *
   * Editable, and separate from `uploadedAt` on purpose. A protokoll from the
   * May general meeting uploaded in September is a May document, and the list a
   * shareholder reads should say so; `uploadedAt` stays as the record of when it
   * reached us, which is an audit fact and not an editable one.
   *
   * Defaults to the day of the upload, so the ordinary case needs no thought.
   */
  date: string
  /** A line saying what it is, where the filename does not. */
  note: string
  /** `shareholderArchive/{id}`. Never built in a browser — see the function. */
  path: string
  size: number
  uploadedAt: string
  uploadedBy: string
}

const str = (v: unknown) => (typeof v === 'string' ? v : '')

export function subscribeToShareholderDocs(
  onData: (docs: ShareholderDoc[]) => void,
  onError: (e: Error) => void,
): () => void {
  return onSnapshot(
    query(collection(db, DOCS)),
    (snap) => {
      onData(
        snap.docs
          .map((d) => ({
            id: d.id,
            name: str(d.get('name')) || 'Document.pdf',
            // Anything filed before the field existed is dated by its upload.
            date: str(d.get('date')) || (toIsoOrNull(d.get('uploadedAt')) ?? '').slice(0, 10),
            note: str(d.get('note')),
            path: str(d.get('path')),
            size: Math.max(0, Number(d.get('size')) || 0),
            uploadedAt: toIsoOrNull(d.get('uploadedAt')) ?? '',
            uploadedBy: str(d.get('uploadedBy')),
          }))
          // Newest first, by the document's own date — an archive is read from
          // the most recent protokoll back, and the date on the paper is the one
          // a reader means by "most recent".
          .sort((a, b) => b.date.localeCompare(a.date) || b.uploadedAt.localeCompare(a.uploadedAt)),
      )
    },
    (e) => onError(e as Error),
  )
}

/**
 * Put a file in the archive.
 *
 * The Firestore document is created first so its id can name the object in
 * Storage. If the upload then fails the document is removed again — an entry
 * pointing at bytes that are not there is worse than no entry, because it looks
 * like a document the company is withholding rather than one nobody filed.
 */
export async function addShareholderDoc(
  file: File,
  note: string,
  uploadedBy: string,
): Promise<void> {
  if (file.type !== ARCHIVE_TYPE) throw new Error('The archive takes PDFs only.')
  if (file.size > ARCHIVE_MAX_BYTES) throw new Error('That file is larger than 20 MB.')

  const created = await addDoc(collection(db, DOCS), {
    name: file.name,
    date: todayIso(),
    note,
    path: '',
    size: file.size,
    uploadedAt: serverTimestamp(),
    uploadedBy,
  })
  const path = `shareholderArchive/${created.id}`
  try {
    await uploadBytes(ref(getStorage(app), path), file, { contentType: ARCHIVE_TYPE })
    await updateDoc(created, { path })
  } catch (e) {
    await deleteDoc(created).catch(() => {})
    throw e
  }
}

export async function renameShareholderDoc(id: string, name: string, note: string): Promise<void> {
  await updateDoc(doc(db, DOCS, id), { name, note })
}

/** The date on the paper, which is not always the day it reached us. */
export async function redateShareholderDoc(id: string, date: string): Promise<void> {
  await updateDoc(doc(db, DOCS, id), { date })
}

/**
 * Take a file out of the archive.
 *
 * The object goes first and the record second. The other order would leave
 * bytes in the bucket that nothing points at and nobody can find to delete.
 */
export async function deleteShareholderDoc(d: ShareholderDoc): Promise<void> {
  if (d.path) {
    // A missing object is the state we are trying to reach, so it is not a
    // failure worth stopping the deletion for.
    await deleteObject(ref(getStorage(app), d.path)).catch(() => {})
  }
  await deleteDoc(doc(db, DOCS, d.id))
}

/**
 * One archived file, saved to the reader's machine.
 *
 * Streamed by `crmShareholderArchive` after it has checked the caller against
 * the allowlist. A shareholder cannot read the Storage path directly and an
 * admin uses the same door, so there is one code path to be wrong about.
 *
 * It downloads rather than opening a tab. Opening one would need a URL the
 * browser can navigate to, and the only URL here is a `blob:` the tab would
 * lose on reload — a download is a file somebody still has tomorrow.
 */
export async function downloadShareholderDoc(id: string, name: string): Promise<void> {
  const blob = await callFunctionForBlob('crmShareholderArchive', { body: { docId: id } })

  /**
   * Saved through an object URL, which is the only route a browser offers.
   *
   * The bytes arrive in a `fetch` because the request needs an `Authorization`
   * header, and a header is the one thing a plain `<a href>` cannot carry. So
   * the file is already in the page by the time there is anything to save, and
   * an anchor over a `blob:` URL is how it leaves.
   */
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = name.toLowerCase().endsWith('.pdf') ? name : `${name}.pdf`
  document.body.append(a)
  a.click()
  a.remove()
  // Not immediately: revoking before the browser has started reading the blob
  // cancels the download in Safari. A minute is far longer than it needs and
  // costs nothing — the page is holding the bytes either way until then.
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
}
